import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {IPriorityDropdownOptionValue, PriorityDropdownOption} from '../../models/IPriorityDropdownOption';
import {IStrategy} from '../../../models/IStrategy';
import {StrtPriority} from '../../models/plan/StrtPriority';
import {PriorityService} from '../../../strategies/services/priority.service';
import * as _ from 'lodash';
import {Observable, Subscription} from 'rxjs';
import {PriorityDropdownComponent} from '../priority-dropdown/priority-dropdown.component';
import {StrtCopyPlanRequest} from '../../models/api/StrtCopyPlanRequest';
import {StrtObjective} from '../../models/plan/StrtObjective';
import {StrtInitiative} from '../../models/plan/StrtInitiative';
import {DataUtils} from '../../utilities/data-utils';
import {AppLoadingService} from '../../../app-loading-panel/app-loading.service';
import {AbstractControl, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ValidationService} from '../../../auth/services/validation.service';
import {timer} from 'rxjs/observable/timer';
import {catchError, distinctUntilChanged, filter, map, switchMap} from 'rxjs/operators';
import {StrategiesService} from '../../../strategies/services/strategies.service';
import {CustomerService} from '../../../auth/services/customer.service';
import {ICustomer} from '../../../auth/models/ICustomer';

@Component({
  selector: 'app-copy-plan-form',
  templateUrl: './copy-plan-form.component.html',
  styleUrls: ['./copy-plan-form.component.scss']
})
export class CopyPlanFormComponent implements OnInit, OnDestroy {
  public readonly MAX_NAME_LENGTH = 80;

  step = 1;
  cbValues: any = {
    includePurposeMissionBeliefs: false,
    includeAllFromOldPlan: false
  };

  oldPlanName = '';

  private _strategy: IStrategy;

  @ViewChild('copyPlanPrioritySelector') prioritySelector: PriorityDropdownComponent;
  @ViewChild('copyPlanObjectiveSelector') objectiveSelector: PriorityDropdownComponent;
  @ViewChild('copyPlanInitiativeSelector') initiativeSelector: PriorityDropdownComponent;

  @Input() institutionId: number;
  @Output() copySuccess = new EventEmitter<string>();
  @Output() strategyLoaded = new EventEmitter<void>();
  startDateValue: Date;
  endDateValue: Date;

  selectOptionsStep1: PriorityDropdownOption[] = [];
  selectOptionsStep2: PriorityDropdownOption[] = [];
  selectOptionsStep3: PriorityDropdownOption[] = [];
  preservedOptions = {
    selectedOptionsStep1: [],
    selectedOptionsStep2: [],
    selectedOptionsStep3: []
  };
  copyPlanForm: FormGroup;
  subscriptions: Subscription[] = [];
  private _priorities: StrtPriority[];

  private _selectedPriorities: StrtPriority[];
  private _selectedObjectives: StrtObjective[];
  private _selectedInitiatives: StrtInitiative[];

  private copyRequest: StrtCopyPlanRequest;
  isDistrict: boolean;
  private allInstitutions: ICustomer[] = [];
  allInstitutionSelected: boolean;

  isLoading$: Observable<boolean>;

  constructor(private priorityService: PriorityService,
              private appLoadingService: AppLoadingService,
              private strategiesService: StrategiesService,
              private validationService: ValidationService,
              private customerService: CustomerService,
              private formBuilder: FormBuilder) { }

  ngOnInit() {
    this.isLoading$ = this.appLoadingService.isLoading$;
    this.createForm();
    this.isDistrict = this.customerService.institution.customerType === 'District';
    this.allInstitutions.push(this.customerService.institution);
    this.allInstitutions.push(... this.customerService.childInstitutions);
  }

  ngOnDestroy(): void {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }

  @Input() set strategy(strategy: IStrategy) {
    if (strategy !== this._strategy) {
      this._strategy = strategy;
    }
  }

  get remainingCharacters() {
    return this.MAX_NAME_LENGTH - (this.name.value ? this.name.value.length : 0);
  }

  get showStrategyNameErrors() {
    return this.name.invalid && (this.name.dirty || this.name.touched);
  }

  loadStrategy() {
    this.reset();
    this.appLoadingService.setLoading();
    this.createForm();
    this.subscriptions.push(this.priorityService.getCopyInfo(this._strategy.strategyId)
      .subscribe((priorities: StrtPriority[]) => {
        this.oldPlanName = this._strategy.strategyName;
        this.name.patchValue(`${this.oldPlanName} - Copy`);
        if (priorities.length === 0) {
          // Plan does not have priorities - skip to step 4
          this.cbValues.includePurposeMissionBeliefs = true;
          this.step = 4;
          setTimeout(() => {
            this.appLoadingService.clearLoading();
            this.strategyLoaded.emit();
          }, 5);
        } else {
          // Plan has priorities - go through normal steps
          _.orderBy(priorities, ['sort'], ['asc']);
          this._priorities = priorities;
          this.cbValues.includePurposeMissionBeliefs = false;
          this.cbValues.includeAllFromOldPlan = false;
          setTimeout(() => {
            this.loadDataForStep(1);
            this.appLoadingService.clearLoading();
            this.strategyLoaded.emit();
            this.step = 1;
          }, 5);
          this.subscriptions.forEach((sub: Subscription) => {
            sub.unsubscribe();
          });
        }
      }));
  }

  private createForm() {
    this.copyPlanForm = this.formBuilder.group({
      name: ['', Validators.required, (control) => this.validateName(control)],
      startDate: [''],
      endDate: ['']
    }, {
      validator: this.validationService.validateDatesInOrder('startDate', 'endDate')
    });
  }

  get showDateErrors() {
    return this.endDate.dirty && this.endDate.invalid;
  }

  private validateName(control: AbstractControl) {
    return timer(50) // wait 3/4 of a second
      .pipe(
        map(() => control.value), // get the current name
        filter(strategyName => !!strategyName), // throw away empty strings
        distinctUntilChanged(), // make sure it changed
        switchMap(strategyName => this.strategiesService.name(this.institutionId, strategyName)),
        map(it => {
          return it && it.duplicateName ? {
            duplicateNameWarning: true
          } : null;
        }),
        catchError((error: any) => {
          console.log(error);
          return null;
        })
      );
  }

  reset() {
    this.step = 1;
    this._selectedPriorities = [];
    this._selectedObjectives = [];
    this._selectedInitiatives = [];
    this.selectOptionsStep1 = [];
    this.selectOptionsStep2 = [];
    this.selectOptionsStep3 = [];
    this.resetDatesAndPlanName();
    // to fully reset the dates, the model needs to be updated
    this.startDateValue = undefined;
    this.endDateValue = undefined;
    this.resetInstitutionSelections();
  }

  resetInstitutionSelections() {
    // reset selected institutions
    this.allInstitutionSelected = false;
    this.allInstitutions.forEach(i => i.selected = false);
  }

  resetDatesAndPlanName() {
    this.startDate.patchValue('');
    this.endDate.patchValue('');
    this.name.patchValue(`${this.oldPlanName} - Copy`);
  }

  get noResultsStep() {
    switch (this.step) {
      case 1:
        return this.selectOptionsStep1.length === 0;
      case 2:
        return this.selectOptionsStep2.length === 0;
      case 3:
        return this.selectOptionsStep3.length === 0;
      default:
          return false;
    }
  }

  get strategy(): IStrategy {
    return this._strategy;
  }

  onCbChange = (event: any, cbFieldName: any) => {
    if (
      event.key === 'Enter' ||
      event.key === ' ' ||
      event.type === 'click'
    ) {
      // don't toggle the menu on enter/space/mouse click
      if (event.type === 'click') {
        this.cbValues[cbFieldName] = !this.cbValues[cbFieldName];
        if (this.cbValues[cbFieldName]) {
          // if one is checked, make sure the other one is unchecked
          if (cbFieldName === 'includeAllFromOldPlan') {
            this.cbValues.includePurposeMissionBeliefs = false;
            // reset priority dropdown selection
            this._selectedPriorities = [];
            this.prioritySelector.deselectEverything();
          } else {
            this.cbValues.includeAllFromOldPlan = false;
          }
        }
        // base component only handles keyboard events
        // so we have to handle it here
      }
    }
  }

  onInstitutionCheckboxChange = (event: any, institution: ICustomer) => {
    if (
        event.key === 'Enter' ||
        event.key === ' ' ||
        event.type === 'click'
    ) {
      // don't toggle the menu on enter/space/mouse click
      if (event.type === 'click') {
        institution.selected = !institution.selected;
      }
    }
  }

  onSelectAllCbChange(event: any) {
    if (
        event.key === 'Enter' ||
        event.key === ' ' ||
        event.type === 'click'
    ) {
      // don't toggle the menu on enter/space/mouse click
      if (event.type === 'click') {
        this.allInstitutionSelected = !this.allInstitutionSelected;
      }
    }
  }

  get name() {
    return this.copyPlanForm.get('name');
  }

  get startDate() {
    return this.copyPlanForm.get('startDate');
  }

  get endDate() {
    return this.copyPlanForm.get('endDate');
  }

  loadDataForStep(stepNum: number) {
    switch (stepNum) {
      case 1:
        this.selectOptionsStep1 = PriorityDropdownOption.fromPriorities(this._priorities);
        break;
      case 2:
        // preserve previous steps options
        this.preservedOptions.selectedOptionsStep1 = this.prioritySelector.selectedOptions;
        // get selected priority ids
        const priorityIds: number[] = this.prioritySelector.selectedOptions.map((item: IPriorityDropdownOptionValue) => {
          return item.value;
        });
        // Check to see if it was select all
        if (priorityIds.length === 1 && priorityIds[0] === -1) {
          // assign all priorities
          this._selectedPriorities = this._priorities;
        } else {
          // assign individually selected priorities
          this._selectedPriorities = this._priorities.filter((priority: StrtPriority) => {
            return priorityIds.indexOf(priority.priorityId) >= 0;
          });
        }
        // clear out objectives and initiatives
        this._selectedObjectives = [];
        this._selectedInitiatives = [];
        // create select options for selected priorities
        this.selectOptionsStep2 = PriorityDropdownOption.fromPriorityObjectives(this._selectedPriorities, false);
        break;
      case 3:
        // get selected priority ids
        if (this.objectiveSelector) {
          // preserve previous steps options
          this.preservedOptions.selectedOptionsStep2 = this.objectiveSelector.selectedOptions;
          const objectiveIds: number[] = this.objectiveSelector.selectedOptions.map((item: IPriorityDropdownOptionValue) => {
            return item.value;
          });
          // assign selected objectives
          this._selectedObjectives = DataUtils.flatten(this._selectedPriorities.map((priority: StrtPriority) => {
            if (objectiveIds.length === 1 && objectiveIds[0] === -1) {
              // return all objectives
              return priority.objectives;
            } else {
              // return individually selected objectives
              return priority.objectives.filter((objective: StrtObjective) => {
                return objectiveIds.indexOf(objective.objectiveId) >= 0;
              });
            }
          }));

          const priorityCopy = JSON.parse(JSON.stringify(this._selectedPriorities));
          const reducedHierarchy = DataUtils.flatten(
            priorityCopy.map((priority: StrtPriority) => {
              if (objectiveIds.length === 1 && objectiveIds[0] === -1) {
                // select all - Don't reduce
              } else {
                priority.objectives = priority.objectives.filter((objective: StrtObjective) => {
                  return objectiveIds.indexOf(objective.objectiveId) >= 0;
                });
              }
              return priority;
          }));

          // create select options for selected priorities
          this.selectOptionsStep3 = PriorityDropdownOption.fromPriorityInitiatives(reducedHierarchy, false);
        } else {
          // create select options for selected priorities
          this.selectOptionsStep3 = [];
        }
        this._selectedInitiatives = [];
        break;
      case 4:
        this.resetDatesAndPlanName();
        if (this.initiativeSelector) {
          // preserve previous steps options
          this.preservedOptions.selectedOptionsStep3 = this.initiativeSelector.selectedOptions;
          const initiativeIds: number[] = this.initiativeSelector.selectedOptions.map((item: IPriorityDropdownOptionValue) => {
            return item.value;
          });
          // assign selected objectives
          this._selectedInitiatives = DataUtils.flatten(this._selectedObjectives.map((objective: StrtObjective) => {
            if (initiativeIds.length === 1 && initiativeIds[0] !== -1) {
              return objective.initiatives.filter((initiative: StrtInitiative) => {
                return initiativeIds.indexOf(initiative.initiativeId) >= 0;
              });
            } else {
              return objective.initiatives;
            }
          }));
        } else {
          this._selectedInitiatives = [];
        }
        break;
      case 5:
        console.log('step 5 - no data loading needed.');
        break;
      default:
        console.log(`Bad step number in loadDataForStep = ${stepNum}`);
        break;
    }
  }

  navForward() {
    if (this.step < 4 || this.isDistrict && this.step < 5) {
      if (this.step === 1 && this.cbValues.includeAllFromOldPlan) {
        this.loadDataForStep(4);
        this.step = 4;
      } else {
        // load data first
        this.loadDataForStep(this.step + 1);
        // update ref template uses to re-render
        this.step += 1;
      }
    }
  }

  executeBackwardReselection(preservedOptions: IPriorityDropdownOptionValue[], selector: PriorityDropdownComponent) {
    if (preservedOptions.length === 1 &&
      preservedOptions[0].value === -1) {
      selector.manualSelectAll();
    } else {
      selector.selectItemsWithIds(preservedOptions
        .map((option: IPriorityDropdownOptionValue) => {
          return option.primaryId;
        }));
    }
  }

  navBack() {
    if (this.step > 1) {
      if (this.step === 4 && this.cbValues.includeAllFromOldPlan) {
        // when includeAllFromOldPlan is selected, steps 2 and 3 are skipped going both forward and backward
        this.step = 1;
      } else {
        this.step -= 1;
      }
      setTimeout(() => {
        switch (this.step) {
          case 1:
            this.executeBackwardReselection(this.preservedOptions.selectedOptionsStep1, this.prioritySelector);
            break;
          case 2:
            this.executeBackwardReselection(this.preservedOptions.selectedOptionsStep2, this.objectiveSelector);
            break;
          case 3:
            this.executeBackwardReselection(this.preservedOptions.selectedOptionsStep3, this.initiativeSelector);
            break;
          default:
            break;
        }
      }, 10);
    }
  }

  onPriorityDropdownSelect(event) {
    this.cbValues.includeAllFromOldPlan = false;
  }

  onObjectiveDropdownSelect(event) {
  }

  onInitiativeDropdownSelect(event) {
  }

  get dropdownPanelClass(): string[] {
    return ['copy-plan-selector-width'];
  }

  get isSubmitDisabled() {
    const nameErrorKeys = this.name.errors ? Object.keys(this.name.errors) : [];
    const hasActualNameError = (nameErrorKeys.length > 1 ||
      (nameErrorKeys.length === 1 && nameErrorKeys.indexOf('duplicateNameWarning') === -1));
    if (this.step === 4) {
      return this.startDate.errors || this.endDate.errors || !this.name.value || this.name.value.trim().length === 0 || hasActualNameError;
    } else {
      // step 5
      return !(this.allInstitutionSelected || this.allInstitutions.find(i => i.selected));
    }
  }

  get isNextDisabled() {
    if (this.step === 1) {
      return !(this.prioritySelector && this.prioritySelector.selectedOptions.length > 0) && !this.cbValues.includeAllFromOldPlan;
    } else if (this.step === 4) {
      // same rules as submit button on page 4
      return this.isSubmitDisabled;
    } else {
      return false;
    }
  }

  onSubmit() {
    const startDateObj: Date = this.startDate.value;
    const endDateObj: Date = this.endDate.value;
    let startDateArr: number[] = null, endDateArr: number[] = null;
    if (startDateObj) {
      startDateArr = [startDateObj.getFullYear(), startDateObj.getMonth() + 1, startDateObj.getDate()];
    }
    if (endDateObj) {
      endDateArr = [endDateObj.getFullYear(), endDateObj.getMonth() + 1, endDateObj.getDate()];
    }
    this.copyRequest = {
      strategyId: this.strategy.strategyId,
      priorities: this._selectedPriorities.map((priority: StrtPriority) => {
        return priority.priorityId;
      }) || [],
      objectives: this._selectedObjectives.map((objective: StrtObjective) => {
        return objective.objectiveId;
      }) || [],
      initiatives: this._selectedInitiatives.map((initiative: StrtInitiative) => {
        return initiative.initiativeId;
      }) || [],
      missionVisionBelief: this.cbValues.includePurposeMissionBeliefs,
      includeAllFromOldPlan: this.cbValues.includeAllFromOldPlan,
      planName: this.name.value,
      targetStartDate: startDateArr,
      targetEndDate: endDateArr,
      institutionIds: this.allInstitutions.filter(i => i.selected || this.allInstitutionSelected).map(i => i.id)
    };

    this.appLoadingService.setLoading();
    this.strategiesService.copyStrategy(this.copyRequest).subscribe((result: IStrategy) => {
      this.appLoadingService.clearLoading();
      this.copySuccess.emit(this.copyRequest.planName);
    }, (err) => {
      console.error('Caught error submitting copy request', err);
      this.appLoadingService.clearLoading();
    });
  }
}
